import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const POLL_MS = 5000;
const MAX_LEN = 500;

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 30_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function TitleChat({ tmdbId, mediaType }) {
  const { user } = useAuth();
  const room = `tmdb:${mediaType}:${tmdbId}`;
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const stickToBottomRef = useRef(true);

  // Initial + polling
  useEffect(() => {
    if (!tmdbId || !mediaType) return;
    let cancel = false;
    let timer;

    const load = async () => {
      try {
        const { data } = await api.get("/chat", { params: { room, limit: 80 } });
        if (cancel) return;
        setMessages(data);
      } catch {
        if (!cancel && messages === null) setMessages([]);
      } finally {
        if (!cancel) timer = setTimeout(load, POLL_MS);
      }
    };
    load();
    return () => { cancel = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tmdbId, mediaType]);

  // Track if user is at bottom (so we only auto-scroll if they're already there)
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const submit = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    if (!user) {
      toast.error("Sign in to chat");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/chat", { text: body }, { params: { room } });
      setMessages((m) => [...(m || []), data]);
      setText("");
      stickToBottomRef.current = true;
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) toast.error(detail || "Slow down");
      else if (status === 401) toast.error("Sign in to chat");
      else toast.error(detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const remove = async (mid) => {
    try {
      await api.delete(`/chat/${mid}`);
      setMessages((m) => (m || []).filter((x) => x.message_id !== mid));
    } catch {
      toast.error("Couldn't delete");
    }
  };

  return (
    <div
      className="rounded-2xl bg-[var(--tt-surface)]/70 border border-[var(--tt-border)] overflow-hidden flex flex-col"
      data-testid="title-chat"
      style={{ height: "min(60vh, 540px)" }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--tt-border)] bg-black/40">
        <MessageCircle className="w-4 h-4 text-[var(--tt-primary)]" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--tt-text-muted)]">
          Live discussion
        </span>
        {messages && (
          <span className="ml-auto text-[10px] font-mono-tt text-[var(--tt-text-faint)]">
            {messages.length} msg{messages.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5"
        data-testid="title-chat-feed"
      >
        {messages === null ? (
          <div className="flex items-center justify-center py-8 text-[var(--tt-text-muted)] text-xs">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-[var(--tt-text-faint)] text-xs py-10">
            No messages yet. Be the first to share a thought.
          </div>
        ) : (
          messages.map((m) => {
            const mine = user && m.user_id === user.user_id;
            const isMod = user && (user.role === "admin" || user.role === "mod");
            return (
              <div
                key={m.message_id}
                data-testid={`title-chat-msg-${m.message_id}`}
                className={`flex gap-2 group ${mine ? "flex-row-reverse" : ""}`}
              >
                <div
                  className="w-7 h-7 rounded-full flex-none bg-[var(--tt-surface-2)] flex items-center justify-center text-[11px] font-extrabold uppercase overflow-hidden"
                  style={{
                    background: m.user_role === "admin"
                      ? "linear-gradient(135deg,#FFD700,#FF8B00)"
                      : "linear-gradient(135deg,#2A2A2A,#1A1A1A)",
                    color: m.user_role === "admin" ? "#000" : "#fff",
                  }}
                >
                  {m.user_picture ? (
                    <img src={m.user_picture} alt="" className="w-full h-full object-cover" />
                  ) : (m.user_name || "?").slice(0, 1)}
                </div>
                <div className={`max-w-[78%] ${mine ? "items-end text-right" : ""}`}>
                  <div className="flex items-baseline gap-2 mb-0.5 text-[10px] text-[var(--tt-text-muted)] font-mono-tt">
                    {!mine && (
                      <span className={`font-bold ${m.user_role === "admin" ? "text-[var(--tt-yellow)]" : "text-white"}`}>
                        {m.user_name}
                      </span>
                    )}
                    <span>{timeAgo(m.created_at)}</span>
                  </div>
                  <div
                    className={`inline-block px-3 py-2 rounded-2xl text-sm leading-snug break-words ${mine
                      ? "bg-[var(--tt-primary)]/20 border border-[var(--tt-primary)]/30 text-white rounded-br-sm"
                      : "bg-[var(--tt-surface-2)] border border-[var(--tt-border)] text-white rounded-bl-sm"}`}
                  >
                    {m.text}
                  </div>
                </div>
                {(mine || isMod) && (
                  <button
                    onClick={() => remove(m.message_id)}
                    data-testid={`title-chat-delete-${m.message_id}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity self-center text-[var(--tt-text-faint)] hover:text-red-400"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={submit}
        className="flex items-center gap-2 px-3 py-2.5 border-t border-[var(--tt-border)] bg-black/40"
        data-testid="title-chat-form"
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
          placeholder={user ? "Say something…" : "Sign in to chat"}
          disabled={!user || sending}
          data-testid="title-chat-input"
          className="flex-1 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full px-4 py-2 text-sm outline-none focus:border-[var(--tt-primary)] disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!user || !text.trim() || sending}
          data-testid="title-chat-send"
          className="flex-none w-9 h-9 rounded-full bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] disabled:opacity-40 flex items-center justify-center"
          aria-label="Send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
